import { Injectable } from '@angular/core';
import { Query, toBoolean } from '@datorama/akita';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import type { ProfileHydrationStatus, SessionState } from './session.model';
import { SessionStore } from './session.store';

import type { BusinessUser } from '../../../../../../api/src/business/business-user.entity';
import { BusinessRole, REPORTING_ADMIN_BUSINESS_ROLES } from '../../../../../../api/src/business/models/business-role';
import type { PublicBusiness } from '../../../../../../api/src/business/business.entity';
import { canManageNotificationPreferences, isUserRoleAtLeast, UserRole } from '../../../../../../api/src/user/models/user-role';

export interface NotificationSettingsAccessSnapshot {
	profileHydrationStatus: ProfileHydrationStatus;
	available: boolean;
}

@Injectable({
	providedIn: 'root'
})
export class SessionQuery extends Query<SessionState> {
	isLoggedIn$ = this.select(state => toBoolean(state.token));
	name$ = this.select(state => state.name);
	profile$ = this.select(state => state.profile);
	notificationSettingsAccess$: Observable<NotificationSettingsAccessSnapshot> = this.select(state => ({
		profileHydrationStatus: state.profileHydrationStatus,
		available: this.hasNotificationSettingsAccess(state)
	}));
	notificationSettingsAvailable$ = this.notificationSettingsAccess$.pipe(
		map(access => access.profileHydrationStatus === 'ready' && access.available),
		distinctUntilChanged()
	);

	/**
	 * Reactive variant of getAdministeredBusinessIds(), for the hydration-safe
	 * reporting guard / sidebar. Emits whenever the profile changes.
	 */
	administeredBusinessIds$: Observable<string[]> = this.select(state => state.profile).pipe(map(() => this.getAdministeredBusinessIds()));

	constructor(protected override store: SessionStore) {
		super(store);
	}

	isLoggedIn() {
		return toBoolean(this.getValue().token);
	}

	getEmailInput() {
		return this.getValue().ui.emailInput;
	}

	getProfile() {
		return this.getValue().profile;
	}

	getBusinesses() {
		return this.getValue().profile?.businesses;
	}

	getToken() {
		return this.getValue().token;
	}

	isReviewer() {
		return this.getValue()?.profile?.reviewer ?? false;
	}

	hasReportingPermission() {
		return this.getValue().profile?.reporting;
	}

	isAtLeastAdmin() {
		const role = this.getValue()?.profile?.role;
		if (!role) {
			return false;
		}
		return isUserRoleAtLeast(role, UserRole.Admin);
	}

	isAtLeastSuperAdmin() {
		const role = this.getValue()?.profile?.role;
		if (!role) {
			return false;
		}
		return isUserRoleAtLeast(role, UserRole.SuperAdmin);
	}

	isOwnerOfBusiness(businessId: string) {
		return this.getValue()?.businessUser?.find(user => user.businessesId === businessId)?.role === BusinessRole.Owner ?? false;
	}

	isFinanceUser(businessId: string) {
		return this.getValue()?.businessUser?.find(user => user.businessesId === businessId)?.role === BusinessRole.Finance ?? false;
	}

	isBusinessAdmin(businessId: string) {
		return this.getValue()?.businessUser?.find(user => user.businessesId === businessId)?.role === BusinessRole.Administrator ?? false;
	}

	isApproverOfBusiness(businessId: string) {
		return this.getValue()?.businessUser?.find(user => user.businessesId === businessId)?.isApprover ?? false;
	}

	hasAdEditorPermission(): boolean {
		// check if at least one business has ad-editor permission
		return !!this.getValue()?.businessUser?.find((user: BusinessUser) => !!user.adEditor);
	}

	/**
	 * The set of business IDs the current user administers, for scoped reporting
	 * access (tier 2). Mirrors the API `BusinessUserUtils.getAdministeredBusinessIds`
	 * as closely as the session payload allows.
	 *
	 * Derived from `profile.businesses` (PublicBusiness[]), which is the only
	 * session source that carries role + business-level soft-delete + internal
	 * flags together:
	 *  - role ∈ {Owner, Administrator},
	 *  - business not soft-deleted (`delete !== true`),
	 *  - business not internal (`internalBusiness !== true`),
	 *  - the membership is not deactivated (cross-referenced from `businessUser`
	 *    by `businessesId`, since `PublicBusiness` does not carry `deactivated`).
	 *
	 * NOTE: the server remains authoritative — it recomputes this set from
	 * auth-time memberships on every request and the scoped option list (chips)
	 * arrives server-scoped. This helper only gates visibility/navigation.
	 */
	getAdministeredBusinessIds(): string[] {
		const businesses = (this.getValue()?.profile?.businesses as PublicBusiness[]) || [];
		const memberships = this.getValue()?.businessUser || [];

		const deactivatedBusinessIds = new Set(memberships.filter(bu => bu?.deactivated === true).map(bu => bu.businessesId));

		const ids = businesses
			.filter(business => !!business)
			.filter(business => REPORTING_ADMIN_BUSINESS_ROLES.includes(business.role as BusinessRole))
			.filter(business => business.delete !== true)
			.filter(business => business.internalBusiness !== true)
			.filter(business => !deactivatedBusinessIds.has(business.id))
			.map(business => business.id)
			.filter(id => !!id);

		// De-duplicate while preserving order.
		return Array.from(new Set(ids));
	}

	/**
	 * Whether the current user administers at least one eligible business — i.e.
	 * qualifies for tier-2 (scoped) reporting access when the site toggle is on.
	 */
	hasAdministeredBusinesses(): boolean {
		return this.getAdministeredBusinessIds().length > 0;
	}

	canPurchaseForBusiness(businessId: string) {
		return (
			(this.getValue()?.businessUser?.find(user => user.businessesId === businessId)?.canPurchase ||
				this.getValue()?.businessUser?.find(user => user.businessesId === businessId)?.role === BusinessRole.Owner ||
				this.getValue()?.profile?.role === UserRole.SuperAdmin) ??
			false
		);
	}

	isGuestOfBusiness(businessId: string): boolean {
		const businessUser = this.getValue()?.businessUser?.find(user => user.businessesId === businessId);
		return businessUser?.role === BusinessRole.Guest;
	}

	canManageGuestNotificationsForBusiness(businessId: string): boolean {
		if (this.isAtLeastAdmin()) {
			return true;
		}

		const membership = this.getValue().businessUser?.find(user => user.businessesId === businessId);
		if (!membership || membership.deactivated === true) {
			return false;
		}

		return [BusinessRole.Owner, BusinessRole.Administrator].includes(membership.role);
	}

	isGuestUser(): boolean {
		return !this.hasNotificationSettingsAccess(this.getValue());
	}

	private hasNotificationSettingsAccess(state: SessionState): boolean {
		const deactivatedBusinessIds = new Set(
			(state.businessUser || []).filter(membership => membership.deactivated === true).map(membership => membership.businessesId)
		);
		const memberships = (state.profile?.businesses || []).map(business => ({
			role: business.role,
			delete: business.delete,
			deactivated: deactivatedBusinessIds.has(business.id)
		}));

		return canManageNotificationPreferences(state.profile?.role, memberships);
	}
}
